import { publicApi } from './public-client';
import {
  IdFlow,
  IdRecordBody,
  IdReview,
  IdScanMode,
  IdScanResult,
  PersonalLink,
  postIdPhoto,
  StayConsentWords,
  StayGuestPolicy,
  StayPublicError,
} from './stays-public-client';
import { AxiosError } from 'axios';

/**
 * LOBBERO STAYS — the stay guest's FULL check-in client (plan S6;
 * lobbero_be/src/stays/routes/checkin-public.js). Public: the token in the path
 * is the whole credential; this rides `publicApi` like S3's client and throws
 * the same `StayPublicError`.
 *
 * People are addressed by their PLACE in the party (lead = 0), never by an id.
 * The answer of every write carries the server's `steps`: the page never
 * computes one. Never log a token.
 */

export type CheckinStepId = 'trip' | 'identity' | 'rules' | 'desk_share' | 'payment' | 'done';
export type CheckinStepState = 'done' | 'current' | 'locked' | 'not_required';
export type CheckinStage = 'checkin' | 'ready' | 'arrival_day' | 'in_stay' | 'ended';
export type AgeBand = 'adult' | 'minor_14_17' | 'minor_under_14';
export type IdentityMode = 'vendor' | 'declared' | 'in_person' | 'not_required';
export type TravelPurpose = 'leisure' | 'business' | 'family_visit' | 'medical' | 'study' | 'other';
export type Relationship =
  | 'parent'
  | 'legal_guardian'
  | 'grandparent'
  | 'sibling'
  | 'other_relative'
  | 'authorised_adult'
  | 'other';

export const TRAVEL_PURPOSES: TravelPurpose[] = ['leisure', 'business', 'family_visit', 'medical', 'study', 'other'];
export const RELATIONSHIPS: Relationship[] = [
  'parent',
  'legal_guardian',
  'grandparent',
  'sibling',
  'other_relative',
  'authorised_adult',
  'other',
];

/** IS7: only on a stay whose listing is linked to a building's front desk. */
export interface DeskShare {
  building_name: string;
  given: boolean;
  /** Check-in cannot finish until this is given. */
  required: boolean;
  version: string;
  text: string;
}

export interface CheckinStep {
  id: CheckinStepId;
  state: CheckinStepState;
}

export interface CheckinMember {
  position: number;
  is_lead: boolean;
  first_name: string;
  last_initial: string;
  age_band: AgeBand | null;
  identity_mode: IdentityMode;
  identity: 'not_started' | 'pending' | 'verified' | 'failed';
  relationship: Relationship | null;
  travels_with: number | null;
  declared: boolean;
  locked: boolean;
  attempts_left: number | null;
  consent_given: boolean;
  personal_link: boolean;
  /** IS0: the in-house check's outcome (null = none recorded) and how the details were given. */
  id_review?: IdReview | null;
  id_method?: 'ocr' | 'manual' | null;
}

export interface CheckinTrip {
  adults: number | null;
  minors: number | null;
  travel_purpose: TravelPurpose | null;
  purpose_required: boolean;
  expected_arrival_time: string | null;
  version: number;
  confirmed: boolean;
}

export interface CheckinCityTax {
  text: string;
  amount_cents?: number;
  currency?: string;
}

export interface CheckinState {
  stage: Exclude<CheckinStage, 'ended'>;
  guest_first_name: string;
  listing: {
    name: string;
    host_name: string;
    building_name: string;
    address: string;
    timezone: string;
    check_in_time: string;
    check_out_time: string;
    max_guests: number;
  };
  stay: { check_in: string; check_out: string; nights: number; building_today: string };
  booked_party: number;
  trip: CheckinTrip;
  rules_summary: { text: string; version: number } | null;
  city_tax: CheckinCityTax | null;
  party: CheckinMember[];
  steps: CheckinStep[];
  completed_at: string | null;
  signed_at: string | null;
  events: { arrived_at: string | null; checked_out_at: string | null; help_today: number };
  consent: StayConsentWords;
  policy: StayGuestPolicy;
  suspended: boolean;
  desk_share?: DeskShare;
  /** IS0: scan or type, and which flow the page runs. */
  id_scan_mode?: IdScanMode;
  id_flow?: IdFlow;
}

export interface CheckinEnded {
  stage: 'ended';
  guest_first_name: string;
  listing: { name: string; timezone: string };
  stay: { check_in: string; check_out: string };
}

export type CheckinAnswer = CheckinState | CheckinEnded;

export interface HouseRules {
  rules_id: string;
  version: number;
  summary: string;
  body: string;
  body_sha256: string;
  agree_text: string;
  consent_version: string;
}

export type SignatureBody =
  | { kind: 'typed'; typed_name: string }
  | { kind: 'drawn'; typed_name: string; drawn_png: string };

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StayPublicError(err.response.status, err.response.data || null, fallback);
    throw new StayPublicError(0, null, fallback);
  }
};

const base = (token: string) => `/stay-links/${encodeURIComponent(token)}`;
const ck = (token: string) => `${base(token)}/checkin`;

export const getCheckin = (token: string): Promise<CheckinAnswer> =>
  call(publicApi.get<CheckinAnswer>(ck(token)), 'Could not open your check-in.');

export const saveTrip = (
  token: string,
  input: {
    version: number;
    adults: number;
    minors: number;
    travel_purpose: TravelPurpose | null;
    expected_arrival_time?: string | null;
  }
): Promise<{ trip: CheckinTrip; steps: CheckinStep[]; party: CheckinMember[]; completed_at: string | null }> =>
  call(publicApi.put(`${ck(token)}/trip`, input), 'Could not save your trip.');

export interface PartyEntry {
  position?: number;
  first_name: string;
  last_name: string;
  birth_date?: string;
}

export const saveParty = (
  token: string,
  guests: PartyEntry[]
): Promise<{ party: CheckinMember[]; steps: CheckinStep[]; completed_at: string | null }> =>
  call(publicApi.put(`${ck(token)}/party`, { guests }), 'Could not save who is coming.');

/**
 * Step 1 in one save (plan ISR): the trip and everyone but the lead. The page
 * never sends adults or children — the server counts them from the dates of
 * birth, the lead being guest 1 and an adult. A kept person goes back as
 * `{position}` only.
 */
export const savePeople = (
  token: string,
  input: {
    version: number;
    guests: Array<PartyEntry | { position: number }>;
    travel_purpose: TravelPurpose | null;
    expected_arrival_time?: string | null;
  }
): Promise<{ trip: CheckinTrip; party: CheckinMember[]; steps: CheckinStep[]; completed_at: string | null }> =>
  call(publicApi.put(`${ck(token)}/people`, input), 'Could not save who is coming.');

export const declareChild = (
  token: string,
  position: number,
  input: { relationship: Relationship | null; travels_with: number | null }
): Promise<{ party: CheckinMember[]; steps: CheckinStep[]; completed_at: string | null }> =>
  call(publicApi.put(`${ck(token)}/guests/${position}/declare`, input), "Could not save their details.");

export const consentFor = (token: string, position: number, version: string, selfie: boolean): Promise<void> =>
  call(publicApi.post(`${ck(token)}/guests/${position}/consent`, { accept: true, version, locale: 'en', selfie }),
    'Could not save your answer.').then(() => undefined);

export const startIdentityFor = (token: string, position: number, selfie: boolean): Promise<{ redirect_url: string }> =>
  call(publicApi.post(`${ck(token)}/guests/${position}/identity`, { selfie }), "We couldn't open the ID check.");

/** IS0: read one person's ID photo (nothing is kept until they confirm). */
export const scanIdFor = (token: string, position: number, photo: Blob): Promise<IdScanResult> =>
  postIdPhoto(`${ck(token)}/guests/${position}/id-scan`, photo);

/** IS0: keep the details the person confirmed. Answers the outcome and the fresh state. */
export const recordIdFor = (
  token: string,
  position: number,
  body: IdRecordBody
): Promise<{ review_state: IdReview; state: CheckinState }> =>
  call(publicApi.post(`${ck(token)}/guests/${position}/id`, body), 'Could not save the ID details.');

export const getPersonalLink = (token: string, position: number): Promise<Omit<PersonalLink, 'shown_once'>> =>
  call(publicApi.get(`${ck(token)}/guests/${position}/personal-link`), 'Could not find their link.');

export const getRules = (token: string): Promise<HouseRules | { none: true }> =>
  call(publicApi.get(`${ck(token)}/rules`), 'Could not open the house rules.');

export const signRules = (
  token: string,
  input: { rules_id: string; body_sha256: string; consent_version: string } & SignatureBody
): Promise<{ signature_id: string; signed_at: string; steps: CheckinStep[]; completed_at: string | null }> =>
  call(publicApi.post(`${ck(token)}/rules/sign`, { ...input, agree: true }), 'Could not sign the house rules.');

export const getRulesPdfUrl = (token: string): Promise<{ url: string }> =>
  call(publicApi.get(`${ck(token)}/rules/pdf`), 'Could not open your copy.');

export const reportEvent = (
  token: string,
  kind: 'arrived' | 'checked_out' | 'help',
  note?: string
): Promise<{ created: boolean }> =>
  call(publicApi.post(`${base(token)}/events`, note ? { kind, note } : { kind }), 'Could not tell your host.');

/** Independent Stays IS7: agree to share with the building's desk, or withdraw. */
export const setDeskShare = (
  token: string,
  decision: 'given' | 'withdrawn',
  version: string
): Promise<{ steps: CheckinStep[]; completed_at: string | null; desk_share: DeskShare | null }> =>
  call(publicApi.post(`${base(token)}/checkin/desk-share`, { decision, version }), 'Could not save your answer.');

export const isEnded = (a: CheckinAnswer): a is CheckinEnded => a.stage === 'ended';
